import React, { useState } from "react";
import {
    Container,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Grid,
    IconButton,
    Card,
    CardContent,
    CardHeader,
    Tooltip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Paper,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Divider,
} from "@mui/material";

import CancelIcon from "@mui/icons-material/Cancel";
import {
    ContentCopy as CopyIcon,
    ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import {
    endpoints,
    errorTypes,
    requestBodies,
    responseExamples,
    statusCodes,
} from "./ApiReferences";

// Theme-aware header background helper
const headerBg = (theme) =>
    theme.palette.mode === "dark" ? "#1e2a3a" : "#e6f0fa";

// CodeBlock — always dark background, works in both modes
const CodeBlock = ({ children, language }) => (
    <Box
        sx={{
            backgroundColor: "#4a4a4a",
            color: "#f8f8f2",
            padding: 2,
            borderRadius: 1,
            overflowX: "auto",
            fontFamily: "Consolas, Monaco, 'Andale Mono', monospace",
            fontSize: 14,
            lineHeight: 1.5,
            whiteSpace: "pre-wrap",
            marginTop: 2,
        }}
    >
        <Box sx={{ mb: 1, color: "#aaa", fontSize: 12 }}>{language}</Box>
        {children}
    </Box>
);

function generateCurlCommand(url, method, requestBody) {
    let curlCommand = `curl -X ${method} "${url}"`;
    curlCommand += ' \\\n  -H "Content-Type: application/json"';
    curlCommand += ' \\\n  -H "Authorization: Bearer YOUR_AUTH_TOKEN"';
    if (method !== "GET" && requestBody) {
        curlCommand += ` \\\n  -d '${requestBody}'`;
    }
    return curlCommand;
}

function generateJS(url, method, requestBody) {
    let code = `const options = {\n  method: '${method}',\n  headers: {\n    'Content-Type': 'application/json',\n    'Authorization': 'Bearer YOUR_AUTH_TOKEN'\n  }`;
    if (method !== "GET" && requestBody) {
        code += `,\n  body: '${requestBody}'`;
    }
    code += `\n};\n\nfetch('${url}', options)\n  .then(response => response.json())\n  .then(data => console.log(data))\n  .catch(error => console.error('Error:', error));`;
    return code;
}

function generatePython(url, method, requestBody) {
    let code = `import requests\n\nurl = "${url}"\nheaders = {\n    "Content-Type": "application/json",\n    "Authorization": "Bearer YOUR_AUTH_TOKEN"\n}\n`;
    if (method !== "GET" && requestBody) {
        code += `data = '${requestBody}'\n\n`;
        code += `response = requests.${method.toLowerCase()}(url, headers=headers, data=data)\n`;
    } else {
        code += `\nresponse = requests.${method.toLowerCase()}(url, headers=headers)\n`;
    }
    code += `print(response.json())`;
    return code;
}

const ApiDocumentationPortal = () => {
    const [selectedModule, setSelectedModule] = useState("");
    const [selectedMethod, setSelectedMethod] = useState("");
    const [url, setUrl] = useState("");
    const [method, setMethod] = useState("");
    const [requestBody, setRequestBody] = useState("");
    const [selectedLanguage, setSelectedLanguage] = useState("curl");
    const [copySuccess, setCopySuccess] = useState(false);

    const codeLanguages = ["curl", "javascript", "python"];

    const handleModuleChange = (e) => {
        const value = e.target.value;
        setSelectedModule(value);
        setSelectedMethod("");
        setRequestBody("");
        if (value && endpoints[value]) {
            setUrl(endpoints[value][0].url);
            setMethod(endpoints[value][0].method);
        } else {
            setUrl("");
            setMethod("");
        }
    };

    const handleMethodChange = (e) => {
        const value = e.target.value;
        setSelectedMethod(value);
        const selected = endpoints[selectedModule].find(
            (endpoint) => endpoint.name === value
        );
        if (selected) {
            setUrl(selected.url);
            setMethod(selected.method);
            const predefinedBody = requestBodies[selectedModule]?.[value];
            setRequestBody(predefinedBody || "");
        }
    };

    const handleCopyCode = () => {
        let code = "";
        if (selectedLanguage === "curl") code = generateCurlCommand(url, method, requestBody);
        else if (selectedLanguage === "javascript") code = generateJS(url, method, requestBody);
        else if (selectedLanguage === "python") code = generatePython(url, method, requestBody);
        navigator.clipboard.writeText(code);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
    };

    const getCodeExample = () => {
        if (selectedLanguage === "curl") return generateCurlCommand(url, method, requestBody);
        if (selectedLanguage === "javascript") return generateJS(url, method, requestBody);
        if (selectedLanguage === "python") return generatePython(url, method, requestBody);
        return "";
    };

    return (
        <BreadcrumbLayout breadcrumbs={[{ title: "API Endpoints" }]}>
            <BreadcrumbLayout.Paper>
                {/* Forces background to follow dark/light mode */}
                <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
                    <Container maxWidth="lg" sx={{ py: 4 }}>

                        {/* Copy Success Toast */}
                        {copySuccess && (
                            <Box
                                sx={{
                                    position: "fixed",
                                    bottom: 20,
                                    right: 20,
                                    bgcolor: "success.main",
                                    color: "white",
                                    p: 2,
                                    borderRadius: 1,
                                    zIndex: 1000,
                                }}
                            >
                                Code copied to clipboard!
                            </Box>
                        )}

                        {/* Module & Method Selectors */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="module-select-label">Module</InputLabel>
                                    <Select
                                        labelId="module-select-label"
                                        id="module-select"
                                        value={selectedModule}
                                        label="Module"
                                        onChange={handleModuleChange}
                                    >
                                        {Object.keys(endpoints).map((module) => (
                                            <MenuItem key={module} value={module}>
                                                {module}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth disabled={!selectedModule}>
                                    <InputLabel id="method-select-label">Method</InputLabel>
                                    <Select
                                        labelId="method-select-label"
                                        id="method-select"
                                        value={selectedMethod}
                                        label="Method"
                                        onChange={handleMethodChange}
                                        endAdornment={
                                            selectedMethod && (
                                                <IconButton
                                                    aria-label="clear method"
                                                    onClick={() => {
                                                        setSelectedMethod("");
                                                        if (selectedModule && endpoints[selectedModule]) {
                                                            setUrl(endpoints[selectedModule][0].url);
                                                            setMethod(endpoints[selectedModule][0].method);
                                                            setRequestBody("");
                                                        }
                                                    }}
                                                    edge="end"
                                                    sx={{ mr: 1 }}
                                                >
                                                    <CancelIcon />
                                                </IconButton>
                                            )
                                        }
                                    >
                                        {selectedModule &&
                                            endpoints[selectedModule].map((endpoint) => (
                                                <MenuItem key={endpoint.name} value={endpoint.name}>
                                                    {endpoint.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        {/* Endpoint Detail Card */}
                        {selectedModule && selectedMethod && (
                            <Grid container spacing={3} sx={{ mb: 4 }}>
                                <Grid item xs={12}>
                                    <Card elevation={2}>
                                        <CardHeader
                                            sx={{ bgcolor: headerBg, py: 1 }}
                                            titleTypographyProps={{ variant: "body1" }}
                                            title={`${selectedModule} - ${selectedMethod} (${endpoints[selectedModule]?.find(
                                                (ep) => ep.name === selectedMethod
                                            )?.description || ""
                                                })`}
                                        />
                                        <Divider />
                                        <CardContent>
                                            {/* Endpoint URL */}
                                            <Box sx={{ mb: 3 }}>
                                                <Typography variant="body1" fontWeight="bold" gutterBottom>
                                                    Endpoint
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        bgcolor: "action.hover",
                                                        p: 2,
                                                        borderRadius: 1,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            px: 1.5,
                                                            py: 0.75,
                                                            mr: 2,
                                                            bgcolor:
                                                                method === "GET" ? "#4caf50" :
                                                                    method === "POST" ? "#2196f3" :
                                                                        method === "PUT" ? "#ff9800" :
                                                                            method === "DELETE" ? "#f44336" :
                                                                                "#9c27b0",
                                                            color: "white",
                                                            borderRadius: 1,
                                                            fontWeight: "bold",
                                                        }}
                                                    >
                                                        {method}
                                                    </Box>
                                                    <Typography sx={{ fontFamily: "monospace", fontSize: "0.9rem" }}>
                                                        {url}
                                                    </Typography>
                                                </Box>

                                                {/* Pagination / ID hint messages */}
                                                {url.includes("page=1") && url.includes("limit=10") && url.includes("vendor") && url.includes("id=vendorId") ? (
                                                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                                        When <code>page=1</code> and <code>limit=10</code>, the endpoint returns 10 records on a single page.
                                                    </Typography>
                                                ) : (
                                                    <>
                                                        {url.includes("page=1") && url.includes("limit=10") && (
                                                            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                                                When <code>page=1</code> and <code>limit=10</code>, the endpoint returns 10 records on a single page.
                                                            </Typography>
                                                        )}
                                                        {url.includes("vendor") && url.includes("{vendorId}") && (
                                                            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                                                Please provide the actual vendor ID by replacing <code>vendorId</code> with the vendor's unique identifier.
                                                            </Typography>
                                                        )}
                                                        {url.includes("vendor") && url.includes("{categoryId}") && (
                                                            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                                                Please provide the actual vendor ID by replacing <code>categoryId</code> with the vendor's category unique identifier.
                                                            </Typography>
                                                        )}
                                                        {url.includes("vendor") && url.includes("{productId}") && (
                                                            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                                                Please provide the actual vendor ID by replacing <code>productId</code> with the vendor's productId unique identifier.
                                                            </Typography>
                                                        )}
                                                        {url.includes("vendor") && url.includes("{couponId}") && (
                                                            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                                                Please provide the actual vendor ID by replacing <code>couponId</code> with the vendor's couponId unique identifier.
                                                            </Typography>
                                                        )}
                                                    </>
                                                )}
                                            </Box>

                                            {/* Request Body & Response */}
                                            <Box sx={{ mb: 3 }}>
                                                <Grid container spacing={3}>
                                                    {method !== "GET" && requestBody && (
                                                        <Grid item xs={12} md={6}>
                                                            <Box sx={{ bgcolor: headerBg, p: 2, mb: 1 }}>
                                                                <Typography variant="body1" fontWeight="bold">
                                                                    Request Body
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ height: "300px", overflowY: "auto" }}>
                                                                <CodeBlock language="JSON">{requestBody}</CodeBlock>
                                                            </Box>
                                                        </Grid>
                                                    )}

                                                    {responseExamples[selectedModule]?.[selectedMethod] && (
                                                        <Grid item xs={12} md={method !== "GET" && requestBody ? 6 : 12}>
                                                            <Box sx={{ bgcolor: headerBg, p: 2, mb: 1 }}>
                                                                <Typography variant="body1" fontWeight="bold">
                                                                    Response
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ height: "300px", overflowY: "auto" }}>
                                                                <CodeBlock language="JSON">
                                                                    {responseExamples[selectedModule][selectedMethod]}
                                                                </CodeBlock>
                                                            </Box>
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            </Box>

                                            {/* Code Samples */}
                                            <Box>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                        mb: 2,
                                                        bgcolor: headerBg,
                                                        p: 2,
                                                        borderRadius: 1,
                                                    }}
                                                >
                                                    <Typography variant="body1" fontWeight="bold">
                                                        Code Samples
                                                    </Typography>
                                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                                        <FormControl size="small" sx={{ width: 150, mr: 2 }}>
                                                            <InputLabel id="language-select-label">Language</InputLabel>
                                                            <Select
                                                                labelId="language-select-label"
                                                                value={selectedLanguage}
                                                                label="Language"
                                                                onChange={(e) => setSelectedLanguage(e.target.value)}
                                                                size="small"
                                                            >
                                                                {codeLanguages.map((lang) => (
                                                                    <MenuItem key={lang} value={lang}>
                                                                        {lang.charAt(0).toUpperCase() + lang.slice(1)}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                        <Tooltip title="Copy code">
                                                            <IconButton onClick={handleCopyCode} size="small">
                                                                <CopyIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </Box>
                                                <CodeBlock language={selectedLanguage.toUpperCase()}>
                                                    {getCodeExample()}
                                                </CodeBlock>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        )}

                        {/* Authentication Section */}
                        <Grid item xs={12} sx={{ mb: 4 }}>
                            <Accordion defaultExpanded>
                                <AccordionSummary
                                    sx={{ bgcolor: headerBg }}
                                    expandIcon={<ExpandMoreIcon />}
                                >
                                    <Typography variant="h6">Authentication</Typography>
                                </AccordionSummary>
                                <Divider />
                                <AccordionDetails>
                                    <Typography paragraph>
                                        All API requests require authentication. Include an authentication token in the request headers.
                                    </Typography>
                                    <Typography variant="body1" fontWeight="bold" gutterBottom>
                                        Header Format
                                    </Typography>
                                    <CodeBlock language="HTTP">
                                        Authorization: Bearer YOUR_AUTH_TOKEN
                                    </CodeBlock>
                                    <Typography variant="body1" fontWeight="bold" sx={{ mt: 2 }} gutterBottom>
                                        Token Generation
                                    </Typography>
                                    <Typography paragraph>
                                        To generate a token, send a POST request to the authentication endpoint:
                                    </Typography>
                                    <CodeBlock language="JSON">
                                        {generateCurlCommand(
                                            endpoints.Auth[0].url,
                                            endpoints.Auth[0].method,
                                            requestBodies.Auth["Generate Token"]
                                        )}
                                    </CodeBlock>
                                </AccordionDetails>
                            </Accordion>
                        </Grid>

                        {/* Reference Tables */}
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Paper elevation={2}>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            bgcolor: headerBg,
                                            p: 2,
                                            borderBottom: "1px solid",
                                            borderColor: "divider",
                                        }}
                                    >
                                        HTTP Status Codes
                                    </Typography>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: "action.hover" }}>
                                                    <TableCell width="15%"><strong>Code</strong></TableCell>
                                                    <TableCell width="25%"><strong>Name</strong></TableCell>
                                                    <TableCell><strong>Description</strong></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {statusCodes.map((statusCode) => (
                                                    <TableRow key={statusCode.code}>
                                                        <TableCell>{statusCode.code}</TableCell>
                                                        <TableCell>{statusCode.name}</TableCell>
                                                        <TableCell>{statusCode.description}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Paper elevation={2}>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            bgcolor: headerBg,
                                            p: 2,
                                            borderBottom: "1px solid",
                                            borderColor: "divider",
                                        }}
                                    >
                                        Error Types
                                    </Typography>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: "action.hover" }}>
                                                    <TableCell width="30%"><strong>Type</strong></TableCell>
                                                    <TableCell><strong>Description</strong></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {errorTypes.map((errorType) => (
                                                    <TableRow key={errorType.type}>
                                                        <TableCell sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
                                                            {errorType.type}
                                                        </TableCell>
                                                        <TableCell>{errorType.description}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                            </Grid>
                        </Grid>

                    </Container>
                </Box>
            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};

export default ApiDocumentationPortal;